import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CollapseBlock } from '../../../../common/index';
import { Spinner } from '../../../../common/index';
import { routes } from '../../../../../utils/helpers/ApiClient';
import { AppBarCMS } from '../../../../basic/index';
import { useToastMessages } from '../../../../../hooks/useToastMessages';
import {
  useGetErasmusMobilitiesQuery,
  useDeleteErasmusMobilityMutation,
  useSaveErasmusMobilityMutation,
} from '../../../../../redux/services/erasmusMobilities';
import { useGetErasmusChallengesQuery } from '../../../../../redux/services/erasmusChallenges';
import ErasmusMobilitiesTable from './ErasmusMobilitiesTable';
import DocumentsModal from './DocumentsModal/DocumentsModal';
import { isNull } from 'lodash';
import QuestionnaireModal from './ErasmusQuestionnaire/QuestionnaireModal/QuestionnaireModal';
import useIsUserAdmin from '../../../../../hooks/useIsUserAdmin.jsx';
import useIsUserErasmusKoordinator from '../../../../../hooks/useIsUserErasmusKoordinator';

const ErasmusMobilitiesPage = () => {
  const { t } = useTranslation();
  const isAdmin = useIsUserAdmin();
  const isErasmusKoordinator = useIsUserErasmusKoordinator();
  const [questionnaireInPreviewId, setQuestionnaireInPreviewId] =
    useState(null);
  const [mobilityEditId, setmobilityEditId] = useState(null);
  const [initialStepIndex, setInitialStepIndex] = useState(null);
  const [selectedChallenge, setSelectedChallenge] = useState('');
  const { data: erasmusMobilities, isLoading, refetch } = useGetErasmusMobilitiesQuery();
  const { data: erasmusChallenges } = useGetErasmusChallengesQuery();
   const [deleteErasmusMobility, { isSuccess: isDeleteSuccess }] = useDeleteErasmusMobilityMutation();
  const [saveErasmusMobility] = useSaveErasmusMobilityMutation();
  const [mobility, setmobility] = useState(null);

  const { deleteSuccess, saveSuccess, saveFailed, warningMessage } = useToastMessages();

  const availableChallenges = useMemo(() => {
    if (!erasmusChallenges) return [];

    if (isAdmin || isErasmusKoordinator) {
      return erasmusChallenges;
    }

    const allowedChallengeIds = new Set(
      (erasmusMobilities || [])
        .map((mobility) => mobility.application?.challenge_id)
        .filter(Boolean),
    );

    return erasmusChallenges.filter((challenge) =>
      allowedChallengeIds.has(challenge.id),
    );
  }, [erasmusChallenges, erasmusMobilities, isAdmin, isErasmusKoordinator]);

  useEffect(() => {
    if (
      selectedChallenge &&
      !availableChallenges.some(
        (challenge) => challenge.id === selectedChallenge,
      )
    ) {
      setSelectedChallenge('');
    }
  }, [availableChallenges, selectedChallenge]);

  useEffect(() => {
    if (isDeleteSuccess) {
      deleteSuccess();
      refetch();
    }
  }, [isDeleteSuccess]);

  return isLoading ? (
    <Spinner />
  ) : (
    <>
      <AppBarCMS
        title={t('Správa mobilít')}
        backUrl={routes.cmsDashboard}
        backUrlTooltip={t('Nástenka')}
        permissionsBasic={'erasmus-mobilities'}
      />
      <div style={{ margin: '10px' }}>
        <CollapseBlock>
          <ErasmusMobilitiesTable
            erasmusMobilities={erasmusMobilities}
            challengeOptions={availableChallenges}
            selectedChallenge={selectedChallenge}
            setSelectedChallenge={setSelectedChallenge}
            onDelete={record => deleteErasmusMobility(record.id)}
            onDetailClick={record => {
              setmobility(record);
              setInitialStepIndex(
                Number.isInteger(record?.initialStepIndex)
                  ? record.initialStepIndex
                  : null,
              );
              setmobilityEditId(record.id);
            }}
            onPreviewClick={(record) => {
              if (record.questionnaire !== null) {
                setQuestionnaireInPreviewId(record.id);
              } else {
                warningMessage({
                  message: 'Dotazník ešte nebol vyplnený',
                });
              }
            }}
          />
        </CollapseBlock>
      </div>
      {!isNull(mobilityEditId) && mobility ? (
        <DocumentsModal
          visible={!isNull(mobilityEditId)}
          toggleModal={() => {
            setmobilityEditId(null);
            setInitialStepIndex(null);
          }}
          title={t('Dokumenty')}
          data={mobility}
          initialStepIndex={initialStepIndex}
          onSave={async (record) => {
            try {
              const response = await saveErasmusMobility(record).unwrap();
              const updatedMobility = response?.mobility
                ? { ...mobility, ...response.mobility }
                : record.body;
              setmobility(updatedMobility);
              saveSuccess();
              refetch();
              return updatedMobility;
            } catch (error) {
              saveFailed(error);
              return null;
            }
          }}
        />
      ) : null}
      {!isNull(questionnaireInPreviewId) ? (
        <QuestionnaireModal
          visible={!isNull(questionnaireInPreviewId)}
          id={questionnaireInPreviewId}
          toggleModal={() => setQuestionnaireInPreviewId(null)}
          title={t('Detail dotazníka')}
        />
      ) : null}
    </>
  );
};

export default ErasmusMobilitiesPage;
